<template>
    <mode-switcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('tariff_creating')"
        :edit-mode-text="$t('tariff_editing')"
        @reset="tariffEditModalSelected = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :items="tariffsFiltered"
                :headers="headers"
                :loading="isTariffsLoading"
                class="elevation-1 table-server"
                :custom-key-sort="{
                    name: (a, b) => localized(a).localeCompare(localized(b)),
                }"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link" @click="showEditModal(item)">
                        {{ localized(item.name) }}
                    </span>
                </template>
                <template v-slot:item.internalDescription="{item}">
                    {{ localized(item.internalDescription) }}
                </template>
                <template v-slot:item.tags="{item}">
                    {{ item.tags.join(', ') }}
                </template>
                <template v-slot:item.active="{item}">
                    <span>
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </span>
                </template>
                <template v-slot:item.actions="{item}">
                    <span>
                        <v-btn variant="text" size="small" color="primary" @click="copyTariff(item)">
                            {{ $t('copy') }}
                        </v-btn>
                    </span>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <product-prices-tariffs-modal
                :tariff="tariffEditModalSelected"
                :tariffs="tariffs"
                :contract="contract"
                :selected-locale="selectedLocale"
                :price-lists="priceLists"
                :is-price-lists-loading="isPriceListsLoading"
                :price-lists-extras="priceListsExtras"
                :is-price-lists-extras-loading="isPriceListsExtrasLoading"
                :commissions="commissions"
                :is-commissions-loading="isCommissionsLoading"
                :penalties="penalties"
                :is-penalties-loading="isPenaltiesLoading"
                :discounts="discounts"
                :is-discounts-loading="isDiscountsLoading"
                @on-close="onCloseEditModal"
                @on-save="onTariffSave"
                @create-price-list="createPriceList"
                @create-price-list-extra="createPriceListExtra"
                @create-commission="createCommission"
                @create-penalty="createPenalty"
                @create-discount="createDiscount"
            />
        </template>
    </mode-switcher>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Setup} from 'vue-facing-decorator'
    import ProductPricesTariffsModal from '~src/components/supplier/prices/modals/ProductPricesTariffsModal.vue'
    import {clone} from '~/utils/helpers'
    import ProductPricesCommissions from '~/components/supplier/prices/ProductPricesCommissions.vue'
    import ProductPricesDiscounts from '~/components/supplier/prices/ProductPricesDiscounts.vue'

    @Component({
        components: {ProductPricesDiscounts, ProductPricesCommissions, ProductPricesTariffsModal},
    })
    export class ProductPricesTariffs extends Vue {
        @Prop() contract
        @Prop() selectedLocale
        @Prop() priceLists
        @Prop() isPriceListsLoading
        @Prop() priceListsExtras
        @Prop() isPriceListsExtrasLoading
        @Prop() commissions
        @Prop() isCommissionsLoading
        @Prop() penalties
        @Prop() isPenaltiesLoading
        @Prop() discounts
        @Prop() isDiscountsLoading
        @Prop() tariffs
        @Prop() isTariffsLoading
        @Setup(() => useLocalized()) localized: Function

        tariffEditModalSelected = null
        activeFilter = true

        @Emit()
        createPriceList() {}

        @Emit()
        createPriceListExtra() {}

        @Emit()
        createCommission() {}

        @Emit()
        createPenalty() {}

        @Emit()
        createDiscount() {}

        @Emit()
        async onTariffSave() {}

        showEditModal(tariff) {
            this.tariffEditModalSelected = tariff
            this.$refs.modeSwitcher.edit()
        }

        copyTariff(tariff) {
            const tariffCopied = clone(tariff)

            delete tariffCopied.id

            tariffCopied.commissions.forEach(tl => {
                delete tl.id
            })

            tariffCopied.extraServicesPriceLists.forEach(tl => {
                delete tl.id
            })

            tariffCopied.penalties.forEach(tl => {
                delete tl.id
            })

            tariffCopied.priceLists.forEach(tl => {
                delete tl.id
            })

            tariffCopied.secondaryServices.forEach(tl => {
                delete tl.id
            })

            tariffCopied.discountsRule?.discounts?.forEach(d => {
                delete d.id
            })

            this.showEditModal(tariffCopied)
        }

        onCloseEditModal() {
            this.$refs.modeSwitcher.goToTable()
        }

        get headers() {
            return [
                {title: this.$t('name'), value: 'name', sortable: true},
                {title: this.$t('internal_description'), value: 'internalDescription'},
                {title: this.$t('currency'), value: 'currencyCode'},
                {title: this.$t('effective_dates'), value: 'dates'},
                {title: this.$t('tags'), value: 'tags'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('actions'), value: 'actions'},
            ]
        }

        get tariffsFiltered() {
            return this.tariffs.filter(el => this.activeFilter === el.active)
        }
    }

    export default ProductPricesTariffs
</script>
